import { useEffect } from "react";
import { useGetIdentity } from "ra-core";

import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import { LBS_LEAD_STATUSES } from "@/modules/constants/contactStatus";
import type { Contact } from "@/components/atomic-crm/types";
import { getLeadsListPath } from "@/app/routing";
import { useNotificationPrefsContext } from "@/modules/notifications/NotificationPrefsContext";
import {
  LEADS_STALE_DAYS as STALE_DAYS,
  summarizeLeadsDigest,
} from "@/modules/notifications/memberNotificationModel";

const CHECK_MS = 30 * 60 * 1000;

const todayKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

const isAssignedToMember = (lead: Contact, memberId: string | number) =>
  (lead.assigned_member_ids ?? []).some(
    (id) => String(id) === String(memberId),
  ) || String(lead.organization_member_id ?? "") === String(memberId);

/**
 * One "Leads need you today" notification per day (not one per lead). The
 * row's tag is the date, so the first device to check creates it and every
 * other device just sees it in the bell.
 */
export const useLeadsFollowUpNotifications = () => {
  const { identity } = useGetIdentity();
  const { prefs } = useNotificationPrefsContext();
  const enabled = prefs.leads_followup_due || prefs.leads_stale;

  useEffect(() => {
    const memberId = identity?.id;
    const orgId = (identity as { org_id?: number } | undefined)?.org_id;
    if (memberId == null || orgId == null || !enabled) return;
    let lastDay = "";

    const run = async () => {
      const day = todayKey();
      if (day === lastDay) return;
      const tag = `leads-digest-${day}`;
      const { data: existing } = await supabase
        .from("member_notifications")
        .select("id")
        .eq("recipient_member_id", memberId)
        .eq("tag", tag)
        .maybeSingle();
      if (existing) {
        lastDay = day;
        return;
      }

      const { data: leads, error } = await supabase
        .from("contacts_summary")
        .select(
          "id, lead_stage, next_followup_at, last_contacted_at, last_seen, assigned_member_ids, organization_member_id",
        )
        .in("status", [...LBS_LEAD_STATUSES]);
      if (error) return;

      const now = Date.now();
      const staleCutoff = now - STALE_DAYS * 24 * 60 * 60 * 1000;
      let followUps = 0;
      let stale = 0;
      for (const lead of (leads ?? []) as Contact[]) {
        if (lead.lead_stage === "won" || lead.lead_stage === "lost") continue;
        if (!isAssignedToMember(lead, memberId)) continue;
        if (
          prefs.leads_followup_due &&
          lead.next_followup_at &&
          new Date(lead.next_followup_at).getTime() <= now
        ) {
          followUps += 1;
          continue;
        }
        const activity = lead.last_contacted_at ?? lead.last_seen;
        if (
          prefs.leads_stale &&
          activity &&
          new Date(activity).getTime() <= staleCutoff
        ) {
          stale += 1;
        }
      }
      lastDay = day;
      if (!followUps && !stale) return;

      // Unique (recipient, tag): another device may have just created it.
      await supabase.from("member_notifications").upsert(
        {
          org_id: orgId,
          recipient_member_id: memberId,
          category: followUps ? "leads_followup_due" : "leads_stale",
          title: "Leads need you today",
          body: summarizeLeadsDigest(followUps, stale),
          href: getLeadsListPath(),
          tag,
        },
        { onConflict: "recipient_member_id,tag", ignoreDuplicates: true },
      );
    };

    void run();
    const timer = window.setInterval(() => void run(), CHECK_MS);
    return () => window.clearInterval(timer);
  }, [identity, enabled, prefs.leads_followup_due, prefs.leads_stale]);
};
